import * as actions_admin from 'actions/admin-actions';
import * as actions_profiles from 'actions/profiles-actions';
import * as actions_schedule from 'actions/schedule-actions';
import * as actions_service_requests from 'actions/service-requests-actions';
import * as actions_users from 'actions/users-actions';
import * as toolbox from 'components/common/toolbox';
import CustomerForm from 'components/customers/customer/customer-form/customer-form';
import React, { useEffect, useState } from 'react';
import firebase from 'firebase/compat/app';
import moment from 'moment';
import { Button, ButtonGroup } from 'react-bootstrap';
import { FormBuilder, ValidateForm, ModalAlert, ModalForm } from 'enspire-manager-framework';
import { LOG, ADDRESSES, ACTION, ROLES, PERMISSIONS } from 'components/constants';
import { confirmArchive, getProfileName } from 'components/common/toolbox';
import { navMultipleUrls, numPanels } from 'components/common/toolbox';
import { service_request_fields, service_request_form_layout } from './service-request-form-layout';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory, useParams, useLocation } from 'react-router-dom';

const _ = require('lodash');

export default function ServiceRequestForm(props) {
	
	/* Hooks ------------------------------------------------------------------------------------------------------------------------------------*/
	
	const seg = 5;
	const history = useHistory();
	const params = useParams();
	const dispatch = useDispatch();
	const routeLocation = useLocation();

	const admin = useSelector((store) => store.admin);
	const employees = useSelector((store) => store.employees.employees);
	const profiles = useSelector((state) => state.profiles);
	const serviceRequest = useSelector((store) => store.serviceRequests.service_request);
	const serviceRequests = useSelector((store) => store.serviceRequests);
	const settings = useSelector((store) => store.settings);
	const user = useSelector(store => store.users?.user);
	const user_permissions = useSelector(store => store.users?.user_permissions);
	const workOrders = useSelector((store) => store.workOrders);

	const [serviceFormType, setServiceFormType] = useState('service_request');
	const [focusedInput, setFocused] = useState(null);
	const [formError, setFormError] = useState(null);
	const [newCustomerName, setNewCustomerName] = useState('');
	const [original, setOriginal] = useState({});
	const [request, setRequest] = useState(service_request_fields(settings?.work_orders?.default_duration ?? 2, profiles.address_selected?.id, params.technicianId ?? 'unassigned'));
	const [newAddress, setNewAddress] = useState(false);
	
    const canViewMySR = (serviceRequest?.techIds?.includes(user?.profileId) || serviceRequest?.techIds?.includes(user?.email));
	const isOwned = (serviceRequest?.ownerId == user?.email);
	const canEdit = user_permissions[PERMISSIONS.SERVICE_REQUESTS_EDIT.key] || isOwned || 
		(canViewMySR && user_permissions[PERMISSIONS.SERVICE_REQUESTS_ASSIGNED_EDIT.key]) || 
		(!parseInt(params.request_id) && user_permissions[PERMISSIONS.SERVICE_REQUESTS_CREATE.key])
	const canDelete = user_permissions[PERMISSIONS.SERVICE_REQUESTS_DELETE.key] || isOwned;
	
	const fromSchedule = props?.fromSchedule;
	const profile = profiles.profile;
    const segments = routeLocation.pathname.split('/').length;

	/* Effects ------------------------------------------------------------------------------------------------------------------------------------*/

	useEffect(() => {
		if (parseInt(params.request_id)) {
			populateForm();
		};
	}, [params.request_id, serviceRequest]);

	useEffect(() => {
		if (!parseInt(params.request_id)) {
			if (parseInt(params.customer_id)) {

				// Set Customer and Address
				async function setCustomer() {
					let customerName = await getProfileName(params.handle, params.customer_id);
					setRequest(prev => ({ ...prev, customerId: [{ target: customerName, id: params.customer_id }] }));
				}
				async function setAddress() {
					let address;
					if (profile?.addresses?.length ) {
						address = _.find(profile.addresses, { addressTypeId: ADDRESSES.SERVICE.id }); // default to Service Address
						if (!address) address = profile.addresses[0];
					}
					setRequest(prev => ({ ...prev, addressId: address?.id }));
				}

				setCustomer();
				if (!profiles.address_selected?.id) setAddress();
				dispatch(actions_profiles.subProfile(params.handle, params.appId, params.customer_id, 'PROFILE'));
			}
			handleDate('requestDate', request.requestDate);
		}
		return () => {
			dispatch(actions_profiles.addressSelected(null));
			let unsubscribe = profiles.profile_unsubscribe;
			if (typeof unsubscribe === "function") unsubscribe();
		};
	}, [params.request_id, params.customer_id]);

	// If from schedule, set the start and end date to the dragged event
	useEffect(() => {
		if (fromSchedule && params.startDateTime && params.endDateTime) {
			let myServiceRequest = { ...request,
				startDate: moment(params.startDateTime, 'X').toDate(),
				startTime: moment(params.startDateTime, 'X').toDate(),
				endDate: moment(params.endDateTime, 'X').toDate(),
				duration: moment(params.endDateTime, 'X').diff(moment(params.startDateTime, 'X'), 'minutes'),
			};
			setRequest(myServiceRequest);
		}
	}, []);

	/* Handlers ------------------------------------------------------------------------------------------------------------------------------------*/

	const handleChange = (e, value) => {
		let newValue = (value) ? value : e.target.value;
		let form_error = _.filter(formError, (o) => { return o.field !== e.target.name; });
		setFormError(form_error);
		setRequest(prev => ({ ...prev, [e.target.name]: newValue }));
	};
	const handleDate = (field, formDate) => {
		var form_error = _.filter(formError, (o) => { return o.field !== field; });
		setFormError(form_error);
		if (field == 'requestDate') {
			setRequest(prev => ({ ...prev, requestDate: formDate }));
		} else {
			let date = moment((field == 'startDate') ? formDate : request.startDate).format('YYYY-MM-DD');
			let time = moment((field == 'startTime') ? formDate : request.startTime).format('hh:mm A');
			let startDate = moment(`${date} ${time}`).toDate();
			setRequest(prev => ({ ...prev, startDate: startDate, startTime: startDate, endDate: moment(startDate).add(settings?.work_orders?.default_duration ?? 2, 'hours').toDate() }));
		}
	};
	const handleUpload = (field, imageUrl) => {
		setRequest({ ...request, imageUrl });
	};

	/* Typeahead Handlers --------------------------*/

	const handleTypeahead = (field, result) => {
		var form_error = _.filter(formError, (o) => { return o.field !== field; });
		setFormError(form_error);
		if (result?.[0]?.customOption) {
			setNewCustomerName(result[0].target)
			openCustomerForm();
		} else if (result?.[0]?.id) {
			dispatch(actions_profiles.subProfile(params.handle, params.appId, result?.[0]?.id, 'PROFILE'));
			setRequest(prev => ({ ...prev, [field]: [{ target: result?.[0]?.target, id: result?.[0]?.id }] }));
		} else {
			setRequest(prev => ({ ...prev, [field]: [] }));
		}

	};
	const handleAutocomplete = (place) => {
		const number = place.address_components.find((component) => component.types.includes('street_number'))?.long_name;
		const route = place.address_components.find((component) => component.types.includes('route'))?.long_name;
		const address = ((number) ? number : '') + ' ' + ((route) ? route : '');
		const city = place.address_components.find((component) => component.types.includes('locality'))?.long_name;
		const stateLocation = place.address_components.find((component) => component.types.includes('administrative_area_level_1'))?.short_name;
		const zip = place.address_components.find((component) => component.types.includes('postal_code'))?.long_name;

		setRequest(prev => ({ ...prev, addressLine1: address, city, state: stateLocation, zip }));
	};

	/* Actions ------------------------------------------------------------------------------------------------------------------------------------*/

	const populateForm = async () => {
		if (serviceRequest) {
			let customerName = serviceRequest._displayName; //await getProfileName(params.handle, params.customer_id);
			// let vendorName = await getProfileName(params.handle, serviceRequest.vendorId);
			let myServiceRequest = { 
				...serviceRequest, 
				requestDate: serviceRequest.requestDate?.toDate(),
				startDate: serviceRequest.startDate?.toDate(),
				startTime: serviceRequest.startDate?.toDate(),
				duration: moment(serviceRequest.endDate?.toDate()).diff(moment(serviceRequest.startDate?.toDate()), 'minutes'),
				customerId: (serviceRequest.customerId) ? [{ target: customerName, id: serviceRequest.customerId }] : [],
				// vendorId: (serviceRequest.vendorId) ? [{ target: vendorName, id: serviceRequest.vendorId }] : [],
			}
			setRequest(myServiceRequest);
			setOriginal(myServiceRequest);
		};
	};
    const openCustomerForm = () => {

		ModalAlert({
			title: `Customer Type`,
			text: "Please choose a customer type:",
			icon: 'question',
			input: 'radio',
			value: 'residential',
			options: {
				'residential': 'Residential',
				'commercial': 'Commercial',
				'property': 'Prop Mngt',
			},

			// Continue
			confirm_color: '#8FBC8B',
			confirm_text: 'Continue',
			callback_success: async (inputValue) => {
				let location = toolbox.modifyPath(routeLocation?.pathname, seg+4, 'new_customer/' + inputValue);
				history.push({ pathname: location });
			},
			show_cancel: false,
		});
    }
	const archiveRequest = () => {
		dispatch(actions_service_requests.archiveServiceRequest(params.handle, request, (serviceRequestId) => { 
			dispatch(actions_admin.saveActivity(params.handle, LOG.SERVICE_REQUEST.key, ACTION.ARCHIVED.key, params.request_id, request.description ));
			history.go((segments > seg+5) ? (-3) : (-2)); // extra back if Work Order column showing
		}));
	};
	const submitForm = () => {
		var form_error = ValidateForm(request, form_layout);
		setFormError(form_error);

		if (!form_error?.length) {
			let newServiceRequest = { ...request, 
				customerId: (request.customerId?.[0]?.id) ? request.customerId[0].id : null,
				vendorId: (request.vendorId?.[0]?.id) ? request.vendorId[0].id : null,
				type: serviceFormType,
			};
			delete newServiceRequest.duration;
			delete newServiceRequest.startTime;
			
			if (fromSchedule) newServiceRequest.technicianId = workOrders.new_service_dragging?.technicianId;

			dispatch(actions_service_requests.saveServiceRequest(params.handle, newServiceRequest, user, (serviceRequestId, workOrderId) => {
				
				if (fromSchedule) dispatch(actions_schedule.cancelNewEvent());
				let action = (parseInt(params.request_id)) ? ACTION.MODIFIED.key : ACTION.CREATED.key;
				dispatch(actions_admin.saveActivity(params.handle, LOG.SERVICE_REQUEST.key, action, serviceRequestId, request.description, request, original ));

				if (!parseInt(params.request_id)) {
					history.goBack();
					// If not from schedule, navigate to the Work Order Items page
					if (!fromSchedule) {
						setTimeout(() => {
							let location = toolbox.modifyPath( routeLocation?.pathname, 4, `${params.customer_id}/form/0/service_requests/${serviceRequestId}/work_orders/${workOrderId}/items`, 11);
							history.push({ pathname: location });
						}, 250);
					}
				} else {
					history.goBack();
				}
			}));
		}
	};

	/* Constants ------------------------------------------------------------------------------------------------------------------------------------*/

	const typeButtons = 
		<ButtonGroup className="d-flex my-3">
			<Button variant={ (serviceFormType == 'service_request') ? 'primary' : 'default' } onClick={ () => setServiceFormType('service_request') }>Service Request</Button>
			<Button variant={ (serviceFormType == 'service_recurring') ? 'primary' : 'default' } onClick={ () => window.toastr.warning(`This feature is Coming Soon!`) }>Recurring Maintenance</Button>
		</ButtonGroup>

	const technicians = _.filter(employees, (o) => { return o.roles?.includes(ROLES.TECHNICIAN.id) || o.roles?.includes(ROLES.LEAD.id) });
	const existing = (params.request_id > 0);
	const form_layout = service_request_form_layout({
		firebase, 
		fromSchedule,
		handle: params.handle, 
		newAddress,
		profile, 
		service_request: request,
		setNewAddress,
		technicians,
		typeButtons,
	});

	/* Render ------------------------------------------------------------------------------------------------------------------------------------*/

	return (

		<>
			<div className={(params.action == 'new_customer') ? 'invisible' : 'visible'}>
				<ModalForm {...props}
					history={history}
					style={1}
					size={'xl'}
					modal_header={'Service Request'}
					cancel_button_title="Cancel"
					save_button_title={`Save Service Request`}
					submitFormHandler={submitForm}
					confirm_button_disabled={ !canEdit }
					delete_button_title={'Archive'}
					delete_button_callback={(!existing) ? null : () => confirmArchive('Service Request & associated items', archiveRequest)}
					delete_button_disabled={ !canDelete }
					show_spinner={serviceRequests?.service_request_save_pending}
					focusedInput={(window.flutter) ? focusedInput : null}
					platform={admin.flutter_platform}
					keyboardPadding={admin.flutter_keyboard}
					visible={ segments[7] != 'new_customer' }
				>
					{request &&
						<FormBuilder
							callbacks={{
								focus: (event) => setFocused(event.target),
								text: handleChange,
								select: handleChange,
								typeahead: handleTypeahead,
								date: handleDate,
								dropzone: handleUpload,
								autocomplete: handleAutocomplete,
							}}
							form_error={formError}
							form={form_layout}
							record={request}
						/>
					}
				</ModalForm>
			</div>

            {/* Integrate Routes under this component -------------------------------------------------- */}

            { params.action == 'new_customer' &&
				<CustomerForm displayName={newCustomerName} type={ params.customerType } source="service_request"/>
            }
		</>
	);
}